<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
  selectedContactIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['toggleContact']);

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const selectedIdsSet = computed(() => new Set(props.selectedContactIds || []));

const isEmpty = computed(() => props.contacts.length === 0);

const isSelected = id => selectedIdsSet.value.has(id);

const formatDate = timestamp => {
  if (!timestamp) return '-';
  return new Date(timestamp * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const handleSelect = (id, event) => {
  emit('toggleContact', { id, value: event.target.checked });
};
</script>

<template>
  <div class="w-full">
    <div
      v-if="isEmpty"
      class="flex items-center justify-center py-12 text-sm text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.TABLE.EMPTY') }}
    </div>

    <table v-else class="w-full text-sm text-left border-collapse">
      <thead>
        <tr class="border-b border-n-strong">
          <th class="w-10 px-3 py-3 font-medium text-n-slate-10" />
          <th class="px-4 py-3 font-medium text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.NAME') }}
          </th>
          <th class="px-4 py-3 font-medium text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.COMPANY') }}
          </th>
          <th class="px-4 py-3 font-medium text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.EMAIL') }}
          </th>
          <th class="px-4 py-3 font-medium text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.PHONE') }}
          </th>
          <th class="px-4 py-3 font-medium text-n-slate-10">
            {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.LAST_ACTIVITY') }}
          </th>
          <th class="px-4 py-3 font-medium text-n-slate-10 text-right">
            {{ t('CONTACTS_LAYOUT.TABLE.COLUMNS.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="contact in contacts"
          :key="contact.id"
          class="border-b border-n-alpha-1 hover:bg-n-alpha-2 transition-colors cursor-pointer"
          @click="onClickViewDetails(contact.id)"
        >
          <td class="px-3 py-3" @click.stop>
            <Checkbox
              :model-value="isSelected(contact.id)"
              @change="event => handleSelect(contact.id, event)"
            />
          </td>
          <td class="px-4 py-3">
            <div class="flex items-center gap-2">
              <Avatar
                :name="contact.name"
                :src="contact.thumbnail"
                :size="28"
                :status="contact.availabilityStatus"
                hide-offline-status
              />
              <span class="font-medium truncate text-n-slate-12">
                {{ contact.name }}
              </span>
            </div>
          </td>
          <td class="px-4 py-3 text-n-slate-11">
            {{ contact.additionalAttributes?.companyName || '-' }}
          </td>
          <td class="px-4 py-3 text-n-slate-11">
            <span v-if="contact.email" class="truncate" :title="contact.email">
              {{ contact.email }}
            </span>
            <span v-else>-</span>
          </td>
          <td class="px-4 py-3 text-n-slate-11">
            {{ contact.phoneNumber || '-' }}
          </td>
          <td class="px-4 py-3 text-n-slate-11">
            {{ formatDate(contact.lastActivityAt) }}
          </td>
          <td class="px-4 py-3 text-right" @click.stop>
            <Button
              :label="t('CONTACTS_LAYOUT.CARD.VIEW_DETAILS')"
              variant="link"
              size="xs"
              @click="onClickViewDetails(contact.id)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
